import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const DEFAULT_CAMPAIGN_TIMEZONE = 'Etc/UTC';
export const CAMPAIGN_PICKER_FORMAT = 'YYYY-MM-DD HH:mm';
export const CAMPAIGN_API_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

type CampaignDateInput = Dayjs | Date | string | null | undefined;

const normalizeCampaignTimezone = (timezoneValue?: string | null) =>
    timezoneValue || DEFAULT_CAMPAIGN_TIMEZONE;

const hasExplicitOffset = (value: string) => /([zZ]|[+-]\d{2}:\d{2})$/.test(value);

const parseNaiveCampaignDate = (value: string, timezoneValue: string) => {
    const normalizedValue = value.trim().replace('T', ' ');
    const format = normalizedValue.length > 16 ? 'YYYY-MM-DD HH:mm:ss' : CAMPAIGN_PICKER_FORMAT;

    return dayjs.tz(normalizedValue, format, timezoneValue);
};

export const parseCampaignDateTime = (
    value: unknown,
    timezoneValue?: string | null,
): Dayjs | null => {
    if (!value) {
        return null;
    }

    const normalizedTimezone = normalizeCampaignTimezone(timezoneValue);

    if (dayjs.isDayjs(value)) {
        return dayjs.tz(
            value.format(CAMPAIGN_PICKER_FORMAT),
            CAMPAIGN_PICKER_FORMAT,
            normalizedTimezone,
        );
    }

    if (value instanceof Date) {
        return dayjs(value).tz(normalizedTimezone);
    }

    if (typeof value === 'string') {
        return hasExplicitOffset(value)
            ? dayjs(value).tz(normalizedTimezone)
            : parseNaiveCampaignDate(value, normalizedTimezone);
    }

    return null;
};

export const getCampaignNow = (timezoneValue?: string | null, now: CampaignDateInput = dayjs()) => {
    const normalizedTimezone = normalizeCampaignTimezone(timezoneValue);

    if (!now) {
        return dayjs().tz(normalizedTimezone);
    }

    if (dayjs.isDayjs(now) || now instanceof Date) {
        return dayjs(now).tz(normalizedTimezone);
    }

    if (typeof now === 'string') {
        return hasExplicitOffset(now)
            ? dayjs(now).tz(normalizedTimezone)
            : parseNaiveCampaignDate(now, normalizedTimezone);
    }

    return dayjs().tz(normalizedTimezone);
};

export const isCampaignDateTimeInFuture = (
    value: unknown,
    timezoneValue?: string | null,
    now: CampaignDateInput = dayjs(),
) => {
    const parsedValue = parseCampaignDateTime(value, timezoneValue);

    if (!parsedValue) {
        return false;
    }

    return parsedValue.isAfter(getCampaignNow(timezoneValue, now));
};

export const formatCampaignDateTimeForApi = (
    value: unknown,
    timezoneValue?: string | null,
) => parseCampaignDateTime(value, timezoneValue)?.format(CAMPAIGN_API_FORMAT) || null;
